import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useRef } from "react";
import { FileText, MessagesSquare, KeyRound, ArrowUpRight } from "lucide-react";

const SellingProcess = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 80%", "end 20%"],
  });
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 24,
    mass: 0.4,
  });
  const progressHeight = useTransform(smoothProgress, [0, 1], ["0%", "100%"]);
  const progressOpacity = useTransform(scrollYProgress, [0, 0.05, 0.95, 1], [0, 1, 1, 0]);

  const steps = [
    {
      number: "01",
      icon: FileText,
      title: "Sign up free — we're ready when you are",
      body: "Tell us what you need and we'll guide you from day one. Sellers get hands-on help building their listing. Buyers get matched to the right homes immediately. No one figures this out alone.",
      tags: ["Seller", "Buyer"],
    },
    {
      number: "02",
      icon: MessagesSquare,
      title: "We guide every conversation and offer",
      body: "Schedule showings, make offers, negotiate terms — we coach you through all of it. You deal directly with the other party, but you're never guessing. We're right there with you, every message and every decision.",
      tags: ["Seller", "Buyer"],
    },
    {
      number: "03",
      icon: KeyRound,
      title: "Close with confidence — until the keys are handed over",
      body: "Need an attorney, inspector, or title company? We connect you instantly. Closing coordination? We handle it. You have a full team behind you — you just don't pay 3–6% of your home's value for the privilege.",
      tags: ["Seller", "Buyer"],
    },
  ];

  return (
    <section ref={sectionRef} className="relative bg-brand-warm-50 py-12 md:py-16 px-6 overflow-hidden">
      {/* Ambient warmth */}
      <div className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-gold/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-32 w-[400px] h-[400px] rounded-full bg-wood/8 blur-3xl pointer-events-none" />

      {/* Scroll progress rail — subtle, editorial */}
      <motion.div
        style={{ opacity: progressOpacity }}
        className="hidden md:block absolute left-2 lg:left-[max(1rem,calc((100%-1100px)/2-1.5rem))] top-24 bottom-24 w-px bg-ink/10 pointer-events-none"
        aria-hidden="true"
      >
        <motion.div
          style={{ height: progressHeight }}
          className="absolute top-0 left-0 w-full bg-gradient-to-b from-gold via-gold to-gold/60 origin-top"
        />
        <motion.div
          style={{ top: progressHeight }}
          className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gold shadow-[0_0_12px_hsl(var(--gold)/0.6)]"
        />
      </motion.div>

      <div className="relative max-w-[1100px] mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2.5 mb-5">
            <span className="w-8 h-px bg-gold" />
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase text-ink">
              About Us
            </span>
          </div>
          <h2
            className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-ink mb-4"
            style={{ lineHeight: "1.05", letterSpacing: "-0.02em" }}
          >
            We walk with you.
            <br />
            <span className="accent-italic text-gold">Every single step.</span>
          </h2>
          <p className="text-base md:text-lg text-ink-soft leading-[1.65] max-w-[600px] mb-16 font-medium">
            People pay 3–6% because they're afraid of doing it alone. But you're not alone with REALV3 — you have real support at every turn. The only difference is you keep what's yours.
          </p>
        </motion.div>

        {/* Steps grid */}
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 80, scale: 0.92, rotateX: -12 }}
                whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                viewport={{ once: true, margin: "-15%" }}
                transition={{
                  duration: 0.9,
                  delay: i * 0.18,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{ y: -6 }}
                style={{ transformPerspective: 1200 }}
                className="group relative"
              >
                {/* Connector line on desktop */}
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 -right-3 w-6 h-px bg-ink/15 z-0" />
                )}

                <div className="relative h-full bg-canvas border border-ink/10 rounded-2xl p-7 md:p-8 transition-all duration-500 hover:border-gold/40 hover:shadow-lift overflow-hidden">
                  {/* Hover glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gold/0 via-gold/0 to-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Step number — large, ghosted */}
                  <div className="absolute top-4 right-5 font-display text-6xl md:text-7xl font-semibold text-ink/[0.06] leading-none tracking-tight select-none transition-all duration-500 group-hover:text-gold/20">
                    {step.number}
                  </div>

                  <div className="relative">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-xl bg-ink/5 flex items-center justify-center mb-6 transition-all duration-500 group-hover:bg-gold group-hover:scale-110 group-hover:rotate-[-4deg]">
                      <Icon className="w-5 h-5 text-ink transition-colors duration-500 group-hover:text-ink" strokeWidth={1.6} />
                    </div>

                    <h3 className="font-display text-xl md:text-[22px] font-semibold text-ink leading-[1.25] mb-3 tracking-tight">
                      {step.title}
                    </h3>
                    <p className="text-[14px] text-ink-soft leading-[1.65] mb-6 font-medium">
                      {step.body}
                    </p>

                    <div className="flex items-center justify-between pt-5 border-t border-ink/10">
                      <div className="flex flex-wrap gap-1.5">
                        {step.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full bg-ink/5 text-ink-soft transition-colors duration-500 group-hover:bg-ink group-hover:text-canvas"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-ink/30 transition-all duration-500 group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SellingProcess;
